import { Column } from 'primereact/column';
import { InputText } from 'primereact/inputtext';
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { apiRequest } from '@/Helpers/general';
import { DT_REQUESTS_RELOAD_INTERVAL, DT_ROWS_NO } from '@/Config/constants';
import debounce from 'lodash.debounce';
import DatatableTemplate from '@/Components/Templates/DatatableTemplate';
import { Button } from 'primereact/button';
import CaseModal from '@/Components/Modals/CaseModal';
import { RequestsContext } from '@/Components/Context/RequestsContext';
import DsarDownloadButton from '@/Components/Partials/DsarDownloadButton';

/**
 * Requests datatable
 */
const DsarRequestDatatable = () => {
    const searchRequestRef = useRef();

    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedRequests, setSelectedRequests] = useState(null);
    const [rowsPerPage, setRowsPerPage] = useState(DT_ROWS_NO);
    const [first, setFirst] = useState(0);
    const [currentPage, setCurrentPage] = useState(0);
    const [searchRequestInput, setSearchRequestInput] = useState('');
    const [totalRecords, setTotalRecords] = useState(0);
    const [sortField, setSortField] = useState('case_id');
    const [sortOrder, setSortOrder] = useState(-1);
    const [clearSearchIcon, setClearSearchIcon] = useState(false);

    const [statusOptions, setStatusOptions] = useState([]);
    const [showCaseModal, setShowCaseModal] = useState(false);
    const [currentCaseId, setCurrentCaseId] = useState();
    const [intervalVar, setIntervalVar] = useState(null);

    /**
     * Get requests from the api
     */
    const getRequests = () => {
        const requestParams = {
            page: currentPage + 1,
            limit: rowsPerPage,
        };
        setLoading(true);

        // search param
        if (searchRequestInput && searchRequestInput != '') {
            requestParams.search = searchRequestInput;
            setClearSearchIcon(true);
        }

        // hide clear search icon
        if (searchRequestInput == '') {
            setClearSearchIcon(false);
        }

        // sort params
        if (sortField !== null) {
            requestParams.sort_by = sortField;
        }

        if (sortOrder != 0) {
            requestParams.sort_order = sortOrder === -1 ? 'desc' : 'asc';
        }

        apiRequest('get', 'dsar-request/search', {
            data: requestParams,
            onSuccess: (response) => {
                setLoading(false);

                if (response.result) {
                    setRequests(response.result);
                }

                setTotalRecords(response.pagination?.totalEntries ? response.pagination.totalEntries : 0);
            },
            onFailure: () => {
                setLoading(false);
            },
        });
    };

    /**
     * Search for a request
     *
     * @param {object} e event object containing search data
     */
    const searchRequest = (e) => {
        setSearchRequestInput(e.target.value);
        setFirst(0);
        setCurrentPage(0);
    };

    /**
     * Clear the request search
     */
    const clearRequestSearch = () => {
        searchRequestRef.current.value = '';
        setSearchRequestInput('');
        setFirst(0);
        setCurrentPage(0);
    };

    /**
     * Debounce the request search function
     */
    const debounceRequestSearch = useMemo(() => {
        return debounce(searchRequest, 500);
    }, []);

    /**
     * On sort column function
     *
     * @param {object} e event object containing sort data
     */
    const onSort = (e) => {
        setSortField(e.sortField);
        setSortOrder(e.sortOrder);
        setFirst(0);
        setCurrentPage(0);
    };

    /**
     * On change page function
     *
     * @param {object} e event object containing current page related data
     */
    const onPageChange = (e) => {
        setCurrentPage(e.page);
        setFirst(e.first);
        setRowsPerPage(e.rows);
    };

    /**
     * The header of the datatable
     */
    const header = () => {
        return (
            <div>
                <span className="p-input-icon-left search-input-wrap">
                    <i className="pi pi-search pl-2" />
                    <InputText className="narrow-input pr-5 pl-4" onChange={debounceRequestSearch} ref={searchRequestRef} placeholder="Search subject" />
                    {clearSearchIcon && <i className="pi pi-times p-button p-button-sm p-button-danger p-button-text p-1" onClick={clearRequestSearch} />}
                </span>
                <span className="search-message">Search by subject's name, address, type, contact number or registration plate</span>
            </div>
        );
    };

    /**
     * Actions column template
     */
    const actionsBodyTemplate = (data) => {
        return (
            <div className="subject-data-actions">
                {data?.case_id && (
                    <div>
                        <Button
                            className="mr-2 p-button-outlined p-button-sm"
                            icon="pi pi-external-link"
                            onClick={() => {
                                setCurrentCaseId(data.case_id);
                                setShowCaseModal(true);
                            }}
                        >
                            View
                        </Button>
                        <DsarDownloadButton data={data} />
                    </div>
                )}
            </div>
        );
    };

    /**
     * Type column template
     */
    const typeBodyTemplate = (data) => {
        if (data.type === 'claimant') {
            return (
                <div className="status-wrap" style={{ color: '#ffffff', backgroundColor: '#a3ccb0', boxShadow: 'inset 0px 0px 6px 3px #a3ccb0' }}>
                    <span>Claimant</span>
                </div>
            );
        } else if (data.type === 'defendant') {
            return (
                <div className="status-wrap" style={{ color: '#ffffff', backgroundColor: '#8e5090', boxShadow: 'inset 0px 0px 6px 3px #8e5090' }}>
                    <span>Defendant</span>
                </div>
            );
        }
    };

    /**
     * Get the requests again in case the search, page, rows number or sort has been changed
     */
    useEffect(() => {
        getRequests();
        clearInterval(intervalVar);
        setIntervalVar(
            setInterval(() => {
                getRequests();
            }, DT_REQUESTS_RELOAD_INTERVAL)
        );
    }, [searchRequestInput, currentPage, sortField, sortOrder, rowsPerPage]);

    /**
     * Clear the debounce on component unmount
     */
    useEffect(() => {
        return () => {
            debounceRequestSearch.cancel();
        };
    });

    return (
        <RequestsContext.Provider value={{ statusOptions }}>
            <DatatableTemplate
                items={requests}
                header={header}
                rowsPerPage={rowsPerPage}
                loading={loading}
                selectedItems={selectedRequests}
                setSelectedItems={setSelectedRequests}
                emptyMessage="No subjects found."
                totalRecords={totalRecords}
                first={first}
                onPageChange={onPageChange}
                customProps={{
                    className: 'requests-datatable',
                    sortField: sortField,
                    sortOrder: sortOrder,
                    onSort: onSort,
                }}
            >
                <Column className="column-case-id" field="case_id" header="Case ID" sortable />
                <Column className="column-name" field="name" header=" Name" sortable />
                <Column className="column-address" field="address" header="Address" sortable />
                <Column className="column-type" field="type" header="Type" body={typeBodyTemplate} sortable />
                <Column className="column-contact-number" field="contact_number" header="Contact No" sortable />
                <Column className="column-veh-reg" field="vehicle_reg" header="Registration plate" sortable />
                <Column className="column-actions" body={actionsBodyTemplate} />
            </DatatableTemplate>

            <CaseModal visible={showCaseModal} setVisible={setShowCaseModal} caseId={currentCaseId} setCaseId={setCurrentCaseId} disabled={true} />
        </RequestsContext.Provider>
    );
};

export default DsarRequestDatatable;
